import { createContext, useCallback, useContext, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { User, Permission } from '../types'
import { forgetUser, identifyUser } from '../lib/analytics'
import { authClient } from '../lib/authClient'
import { endSession } from '../queries/queryClient'
import { hasPasskeyQuery, sessionKeys, sessionQuery } from '../queries/session'
import Login from '../pages/Login'
import ProfileSetup from '../pages/ProfileSetup'
import { needsDisplayName } from '../lib/profile'

interface AuthContextValue {
	user: User | null
	isLoading: boolean
	hasPermission: (permission: Permission) => boolean
	hasPasskey: boolean
	markPasskeyAdded: () => void
	signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue>({
	user: null,
	isLoading: true,
	hasPermission: () => false,
	hasPasskey: true,
	markPasskeyAdded: () => {},
	signOut: async () => {},
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const queryClient = useQueryClient()
	const session = useQuery(sessionQuery())
	const user = session.data ?? null
	const userId = user?.id ?? ''
	const { data: hasPasskey = true } = useQuery(hasPasskeyQuery(userId))

	useEffect(() => {
		if (user) identifyUser(user)
	}, [user])

	const markPasskeyAdded = useCallback(() => queryClient.setQueryData(sessionKeys.hasPasskey(userId), true), [queryClient, userId])

	const signOut = useCallback(async () => {
		await authClient.signOut()
		forgetUser()
		endSession()
	}, [])

	const refreshSession = useCallback(async () => {
		await queryClient.fetchQuery({ ...sessionQuery(), staleTime: 0 })
	}, [queryClient])

	const hasPermission = (permission: Permission) => user?.permissions.includes(permission) ?? false

	if (session.isPending) return <div className="min-h-screen bg-background" />
	if (!user) return <Login onSignedIn={refreshSession} />
	if (needsDisplayName(user.missing_profile_fields)) return <ProfileSetup email={user.email} onSignOut={signOut} />

	return (
		<AuthContext.Provider value={{ user, isLoading: false, hasPermission, hasPasskey, markPasskeyAdded, signOut }}>
			{children}
		</AuthContext.Provider>
	)
}

export const useAuth = () => useContext(AuthContext)
